import { datadogRum } from '@datadog/browser-rum'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { redactWithSizeCap } from '@dni-api-client/datadog/redact'
import { useApiIsUserRegistered } from '@hooks-api/wp/useApiIsUserRegistered'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { isEmail } from '@utils'
import { removePlusPrefixOfPhone } from '@utils/removePlusPrefixOfPhone'
import { type AxiosError } from 'axios'
import Cookies from 'js-cookie'
import queryString from 'query-string'
import React, { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import { useRequestOtpVerification } from '../../../hooks/apis/wp/useRequestOtpVerification'
import { useCosignCookies } from '../../../hooks/useCosignCookies'
import { type OtpVerificationErrorResponse } from '../../../types/apiErrorResponse'
import { type Otp } from '../../../types/dataModel/platformSdk'
import { getAccount } from '../../../utils/cookie'
import { useContextCosign } from '../../useContextCosign'
import { ErrorMessage } from './ErrorMessage'

const getAccountType = (account: string) => isEmail(account) ? LOGIN_TYPE.EMAIL_OTP : LOGIN_TYPE.MOBILE_OTP

const INCORRECT_VERIFICATION_CODE = 401
const VERIFICATION_CODE_EXPIRED = 404

const styles = stylex.create({
  formContainer: {
    margin: 0,
    display: 'flex',
    justifyContent: 'space-between',
  },
  formControl: {
    margin: 0,
    borderColor: {
      'default': '#DDDDDD',
      ':focus-visible': '#4B4B4B',
      ':focus': '#4B4B4B',
    },
    borderRadius: '4px',
    borderStyle: 'solid',
    borderWidth: 1,
    outline: {
      'default': 'none',
      ':focus-visible': '#4B4B4B',
    },
    backgroundColor: {
      'default': 'transparent',
      ':disabled': colors.grey300,
    },
    boxSizing: 'border-box',
    color: colors.grey800,
    display: 'block',
    fontSize: '16px',
    textAlign: 'center',
    height: 48,
    minWidth: 0,
    width: 50,
  },
  formControlError: {
    margin: 0,
    borderColor: colors.red600,
    minWidth: 0,
  },
})

const handleSelectInputEvent = (event: HTMLInputElement) => {
  setTimeout(() => {
    event.select()
  }, 0)
}

interface InputVerificationProps {
  error: string
  setError: (error: string) => void
  purpose: Otp['purpose']
}

const OTP_LENGTH = 6

export const InputVerification = (props: InputVerificationProps) => {
  const { t } = useTranslation()
  const { error, setError, purpose } = props
  const account = getAccount()
  const canVerify = useRef(true)
  const prevSendOtpCode = useRef('')
  const inputRef = useRef<HTMLInputElement[]>([])
  const [values, setValues] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [isSuccessLogin, setIsSuccessLogin] = useState(false)
  const { environment, campaignCode } = useContextCosign()

  useEffect(() => {
    if (values.every(value => value === '')) {
      // @ts-expect-error - TODO: fix this
      inputRef.current[0].focus()
    }
  }, [values])

  const handleInput = (event: FormEvent<HTMLInputElement>, index: number) => {
    // NOTE: return 掉windows中文輸入法
    // @ts-expect-error - TODO: fix this
    const inputType = event.nativeEvent?.inputType as string
    if (inputType === 'insertCompositionText') {
      event.preventDefault()
      return
    }
    const newValues = [...values]
    const value = (event.target as HTMLInputElement)?.value
    const regex = /^\d+$/
    if (!regex.test(value) && value !== '') {
      event.preventDefault()
      return
    }
    newValues[index] = value.slice(0, 1)
    setValues(newValues)
    if (index < OTP_LENGTH - 1 && value) {
      const nextInput = inputRef.current[index + 1]
      // @ts-expect-error - TODO: fix this
      nextInput.focus()
      // @ts-expect-error - TODO: fix this
      handleSelectInputEvent(nextInput)
    }
    canVerify.current = true
  }

  // NOTE: chrome issues https://github.com/devfolioco/react-otp-input/issues/322
  const handleInputChange = (index: number) => (event: FormEvent<HTMLInputElement>) => {
    // NOTE: return 掉windows中文輸入法
    // @ts-expect-error - TODO: fix this
    const inputType = event.nativeEvent?.inputType as string
    if (inputType === 'insertCompositionText') {
      event.preventDefault()
      return
    }
    const value = (event.target as HTMLInputElement)?.value
    if (index === 0 && value.length === OTP_LENGTH) {
      const pasteData = value.split('').slice(0, OTP_LENGTH)
      setValues(pasteData.concat(Array(OTP_LENGTH - pasteData.length).fill('')))
      pasteData.forEach((_, index) => {
        // @ts-expect-error - TODO: fix this
        if (inputRef.current[index]) inputRef.current[index].value = pasteData[index]
        // NOTE: chrome 會和 handleInput 的有衝突，timeout 時間必須在他之後
        setTimeout(() => {
          // @ts-expect-error - TODO: fix this
          inputRef.current[OTP_LENGTH - 1].select()
          // @ts-expect-error - TODO: fix this
          inputRef.current[OTP_LENGTH - 1].focus()
        }, 10)
      })
      canVerify.current = true
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault()
    const pasteData = event.clipboardData.getData('text').split('').slice(0, OTP_LENGTH)
    setValues(pasteData.concat(Array(OTP_LENGTH - pasteData.length).fill('')))
    pasteData.forEach((_, index) => {
      // @ts-expect-error - TODO: fix this
      if (inputRef.current[index]) inputRef.current[index].value = pasteData[index]
    })
    canVerify.current = true
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !values[index]) {
      const previousIndex = Math.max(index - 1, 0)
      // @ts-expect-error - TODO: fix this
      inputRef.current[previousIndex].focus()
    }
    else if (event.key === 'ArrowLeft') {
      const previousIndex = Math.max(index - 1, 0)
      // @ts-expect-error - TODO: fix this
      inputRef.current[previousIndex].focus()
      setTimeout(() => {
        // @ts-expect-error - TODO: fix this
        inputRef.current[previousIndex].select()
      }, 0)
    }
    else if (event.key === 'ArrowRight') {
      const nextIndex = Math.min(index + 1, 5)
      const nextInput = inputRef.current[nextIndex]
      // @ts-expect-error - TODO: fix this
      nextInput.focus()
      // @ts-expect-error - TODO: fix this
      handleSelectInputEvent(nextInput)
    }
    canVerify.current = true
  }
  const accountType = account ? getAccountType(account) : null
  const identifier = accountType === LOGIN_TYPE.MOBILE_OTP && account ? removePlusPrefixOfPhone(account) : account
  const _loginType = accountType === LOGIN_TYPE.MOBILE_OTP ? 'sms' : 'email'
  const { destructuredData: isUserRegisteredResponse } = useApiIsUserRegistered({
    identifier,
    login_type: _loginType,
  })
  const { trigger: otpVerification, isMutating } = useRequestOtpVerification()
  const { countryCode } = useCosignCookies()
  const { sendEvent, sendPendingEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  const sendGaEvent = useCallback(() => {
    if (countryCode) {
      sendPendingEvent({ eventName: 'country code', eventParams: { event_category: 'Login', country_code: countryCode } })
    }
    if (!accountType || !isUserRegisteredResponse) return
    const { is_registered } = isUserRegisteredResponse
    if (is_registered) {
      sendPendingEvent({ eventName: 'login method', eventParams: { event_category: 'Login', account_type: accountType } })
    }
    else {
      sendPendingEvent({ eventName: 'login method', eventParams: { event_category: 'Signup', account_type: accountType } })
    }
  }, [accountType, countryCode, isUserRegisteredResponse, sendPendingEvent])

  const history = useHistory()
  useEffect(() => {
    const handleVerify = async () => {
      if (values.join('').length !== OTP_LENGTH || !canVerify.current) return
      if (prevSendOtpCode.current === values.join('')) return
      if (!account) return
      const accountType = getAccountType(account)
      try {
        canVerify.current = false
        prevSendOtpCode.current = values.join('')
        const { data } = await otpVerification({
          account,
          otpCode: values.join(''),
          purpose,
        })
        if (purpose === 'login') sendGaEvent()
        if (environment === 'wordPress') {
          window.location.href = data.data.url
        }
        if (environment === 'merchant' && campaignCode) {
          window.location.href = data.data.url
        }
        if (environment === 'campaign') {
          window.location.href = data.data.url
        }
        setError('')
        setIsSuccessLogin(true)
      }
      catch (error) {
        const e = error as AxiosError<OtpVerificationErrorResponse>
        const isExpectedRejection = e.response?.status === INCORRECT_VERIFICATION_CODE || e.response?.status === VERIFICATION_CODE_EXPIRED
        if (e.response?.status === INCORRECT_VERIFICATION_CODE) {
          if (accountType === LOGIN_TYPE.EMAIL_OTP)
            sendEvent({ eventName: 'Email OTP error', eventParams: { event_category: 'Email OTP', email_otp_error: 'incorrect' } })
          if (accountType === LOGIN_TYPE.MOBILE_OTP)
            sendEvent({ eventName: 'mobile OTP error', eventParams: { event_category: 'Mobile OTP', mobile_otp_error: 'incorrect' } })
          setError(t('error.incorrectVerificationCode'))
        }
        if (e.response?.status === VERIFICATION_CODE_EXPIRED) {
          if (accountType === LOGIN_TYPE.EMAIL_OTP)
            sendEvent({ eventName: 'Email OTP error', eventParams: { event_category: 'Email OTP', email_otp_error: 'expired' } })
          if (accountType === LOGIN_TYPE.MOBILE_OTP)
            sendEvent({ eventName: 'mobile OTP error', eventParams: { event_category: 'Mobile OTP', mobile_otp_error: 'expired' } })
          setError(t('error.verificationCodeExpired'))
        }
        if (!isExpectedRejection) {
          try {
            const responseData = e.response?.data
            datadogRum.addError(error, {
              function: 'InputVerification.handleVerify',
              api: {
                key: '/api/platform-sdk/otp-verification',
                status: e.response?.status,
                requestBody: redactWithSizeCap({ purpose, accountType }),
                responseData: responseData === undefined ? undefined : redactWithSizeCap(responseData),
              },
            })
          } catch {
            /* 觀測失敗不影響流程，靜默吞掉 */
          }
        }

        setIsSuccessLogin(false)
        setValues(Array(OTP_LENGTH).fill(''))
        // @ts-expect-error - TODO: fix this
        handleSelectInputEvent(inputRef.current[0])
        console.error(error)
      }
    }
    handleVerify()
  }, [account, campaignCode, environment, sendGaEvent, history, purpose, sendEvent, setError, t, otpVerification, values])

  const search = queryString.parse(window.location.search)
  const otpCodeFromUrl = search?.otpCode
  const emailFromUrl = search?.account
  const handleOtpFromUrl = useCallback(() => {
    if (typeof otpCodeFromUrl !== 'string' || typeof emailFromUrl !== 'string') return
    sendEvent({ eventName: 'Email verify token', eventParams: { event_category: 'Email token loading' } })
    Cookies.set('account', emailFromUrl)
    const splitOtpCode = otpCodeFromUrl.split('')
    setValues(splitOtpCode.slice(0, OTP_LENGTH).concat(Array(OTP_LENGTH - otpCodeFromUrl.length).fill('')))
    window.history.replaceState(null, '', window.location.pathname)
    splitOtpCode.forEach((_, index) => {
      // @ts-expect-error - TODO: fix this
      if (inputRef.current[index]) inputRef.current[index].value = otpCodeFromUrl[index]
    })
  }, [emailFromUrl, otpCodeFromUrl, sendEvent])

  useEffect(() => {
    handleOtpFromUrl()
  }, [handleOtpFromUrl])

  return (
    <div>
      <form {...stylex.props(styles.formContainer)}>
        {values.map((value, index) => (
          <input
            {...stylex.props(
              styles.formControl,
              error ? styles.formControlError : undefined,
            )}
            disabled={isMutating || isSuccessLogin}
            key={index}
            type="tel"
            value={value}
            ref={(ref) => {
              if (ref) {
                inputRef.current.push(ref)
              }
            }}
            onInput={e => handleInput(e, index)}
            onKeyDown={e => handleKeyDown(e, index)}
            onPaste={index === 0 ? handlePaste : undefined}
            onChange={handleInputChange(index)}
            autoComplete="one-time-code"
            inputMode="numeric"
          />
        ))}
      </form>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  )
}
